<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChatEmbed } from '@/services/chat/chatHttp.service'
import { chatApi } from '@/services/api'
import Combobox from '@/components/common/Combobox.vue'
import BOptions from '@/components/common/bOptions.vue'
import ListItemResource from '@/components/resource/autocomplete/ListItem.vue'
import ResourceImage from '@/components/resource/resourceImage.vue'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import { useInfinityResource } from '@/services/resource'
import { useInfinityProject } from '@/services/project'
import { useInfinityEvaluation } from '@/services/evaluation'
import { useInfinityRecruitmentProcess } from '@/services/recruitment'
import { displayLocale } from '@/services/application/displayLocale'

const { t } = useI18n()

const emits = defineEmits<{
  (e: 'select', embed: ChatEmbed): void
  (e: 'close'): void
}>()

const typeOptions = [
  { id: 'resource', display: t('modules.chat.embed.resource') },
  { id: 'process', display: t('modules.chat.embed.candidate') },
  { id: 'project', display: t('modules.chat.embed.project') },
  { id: 'evaluation', display: t('modules.chat.embed.evaluation') },
]

const selectedType = ref(typeOptions[0])
const selectedItem = ref<any>()

const {
  getMoreData: getMoreResource,
  haveMore: haveMoreResource,
  limit: limitResource,
  page: pageResource,
  list: listResource,
  search: searchResource,
  isLoading: isLoadingResource,
} = useInfinityResource({})

const {
  getMoreData: getMoreProject,
  haveMore: haveMoreProject,
  limit: limitProject,
  page: pageProject,
  list: listProject,
  search: searchProject,
  isLoading: isLoadingProject,
} = useInfinityProject({})

const {
  getMoreData: getMoreEvaluation,
  haveMore: haveMoreEvaluation,
  limit: limitEvaluation,
  page: pageEvaluation,
  list: listEvaluation,
  search: searchEvaluation,
  isLoading: isLoadingEvaluation,
} = useInfinityEvaluation({})

const {
  getMoreData: getMoreProcess,
  haveMore: haveMoreProcess,
  limit: limitProcess,
  page: pageProcess,
  list: listProcess,
  search: searchProcess,
  isLoading: isLoadingProcess,
} = useInfinityRecruitmentProcess({})

const dateFormatter = new Intl.DateTimeFormat(displayLocale.value, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

const formatDate = (value?: Date | string | null) => {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return dateFormatter.format(date)
}

const formatDateRange = (start?: Date | string | null, end?: Date | string | null) => {
  const from = formatDate(start)
  const to = formatDate(end)
  if (from && to) return `${from} → ${to}`
  return from || to || ''
}

const resourceOptions = computed(() =>
  listResource.value.map((resource: any) => ({
    ...resource,
    display: resource.display || `${resource.firstname || ''} ${resource.lastname || ''}`.trim(),
  })),
)

const projectOptions = computed(() =>
  listProject.value.map((project: any) => ({
    ...project,
    display: project.display || project.name || 'Projet',
    projectCode: project.projectCode || project.code || '',
    client: project.client || null,
  })),
)

const evaluationOptions = computed(() =>
  listEvaluation.value.map((evaluation: any) => {
    const evaluated = evaluation.resource || null
    const rawEvaluators = evaluation.managedByPopulated || []
    const evaluators = (Array.isArray(rawEvaluators) ? rawEvaluators : [])
      .filter((person: any) => person && typeof person === 'object' && person._id)
    const project = (evaluation.project && typeof evaluation.project === 'object' && evaluation.project._id)
      ? evaluation.project
      : (evaluation.projectId && typeof evaluation.projectId === 'object' && evaluation.projectId.display)
        ? evaluation.projectId
        : null
    const evaluatedName = evaluated
      ? `${evaluated.firstname || ''} ${evaluated.lastname || ''}`.trim()
      : ''
    return {
      ...evaluation,
      display: evaluation.display || evaluatedName || 'Évaluation',
      evaluated,
      evaluators,
      project,
      dateLabel: formatDateRange(evaluation.startDate, evaluation.endDate),
    }
  }),
)

watch(selectedType, () => {
  selectedItem.value = undefined
})

watch(selectedItem, async (item) => {
  if (!item?._id) return
  await pick(item)
  selectedItem.value = undefined
})

const pick = async (item: any) => {
  const type = String(selectedType.value.id) as ChatEmbed['type']
  try {
    const resolved = await chatApi.resolveEmbed({ type, entityId: item._id }).promise
    if (resolved.embed) {
      emits('select', resolved.embed)
      return
    }
  }
  catch {
    // fallback
  }

  const title = item.display
    || (item.firstname || item.lastname
      ? `${item.firstname || ''} ${item.lastname || ''}`.trim()
      : item.candidate
        ? `${item.candidate.firstname || ''} ${item.candidate.lastname || ''}`.trim()
        : '—')

  let subtitle = item.position?.display || item.type || ''
  if (type === 'project') {
    subtitle = [item.client?.display, item.projectCode].filter(Boolean).join(' · ')
  }
  else if (type === 'evaluation') {
    subtitle = [item.project?.display, item.dateLabel].filter(Boolean).join(' · ')
  }

  emits('select', {
    type,
    entityId: item._id,
    snapshot: {
      title,
      subtitle,
      picture: item.picture || item.candidate?.picture || item.evaluated?.picture || null,
    },
  })
}
</script>

<template>
  <div
    class="rounded-xl chat-glass-dense border border-black/10 dark:border-white/10 shadow-lg overflow-visible w-full max-w-md"
    data-cy="chat-embed-picker"
  >
    <div class="p-2 border-b dark:border-white/10 space-y-2">
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs font-medium opacity-70">{{ t('modules.chat.embed.title') }}</span>
        <button
          type="button"
          class="p-1.5 rounded-sm bg-hover"
          data-cy="chat-embed-picker-close"
          @click="emits('close')"
        >
          <MaterialSymbolsCloseRounded class="w-4 h-4" />
        </button>
      </div>
      <BOptions
        v-model="selectedType"
        data-cy="chat-embed-type"
        :list-items="typeOptions"
        size="small"
      />
    </div>

    <div class="p-2 pb-4">
      <Combobox
        v-if="selectedType.id === 'resource'"
        v-model="selectedItem"
        v-model:search="searchResource"
        v-model:limit="limitResource"
        v-model:page="pageResource"
        v-model:have-more="haveMoreResource"
        data-cy="chat-embed-resource-combobox"
        :loading="isLoadingResource"
        :options="resourceOptions"
        :placeholder="t('modules.chat.embed.search')"
        size="small"
        @search="getMoreResource"
      >
        <template #custom-item="{ item }">
          <ListItemResource :resource="item" />
        </template>
        <template #selected-render="{ selected }">
          <ListItemResource :resource="selected" />
        </template>
      </Combobox>

      <Combobox
        v-else-if="selectedType.id === 'project'"
        v-model="selectedItem"
        v-model:search="searchProject"
        v-model:limit="limitProject"
        v-model:page="pageProject"
        v-model:have-more="haveMoreProject"
        :loading="isLoadingProject"
        :options="projectOptions"
        :placeholder="t('modules.chat.embed.search')"
        size="small"
        @search="getMoreProject"
      >
        <template #custom-item="{ item }">
          <div class="min-w-0 flex-1 w-full overflow-hidden flex flex-col gap-0.5">
            <p class="text-[11px] leading-tight truncate">
              <span
                v-if="item.client"
                class="font-medium subtitle"
              >
                {{ item.client.display }}
              </span>
              <span
                v-if="item.client && item.projectCode"
                class="mx-1 opacity-40"
              >·</span>
              <span
                v-if="item.projectCode"
                class="font-mono text-primary-600 dark:text-primary-400"
              >
                {{ item.projectCode }}
              </span>
            </p>
            <p class="text-sm truncate leading-tight">
              {{ item.display }}
            </p>
          </div>
        </template>
      </Combobox>

      <Combobox
        v-else-if="selectedType.id === 'evaluation'"
        v-model="selectedItem"
        v-model:search="searchEvaluation"
        v-model:limit="limitEvaluation"
        v-model:page="pageEvaluation"
        v-model:have-more="haveMoreEvaluation"
        :loading="isLoadingEvaluation"
        :options="evaluationOptions"
        :placeholder="t('modules.chat.embed.search')"
        size="small"
        @search="getMoreEvaluation"
      >
        <template #custom-item="{ item }">
          <div class="min-w-0 flex-1 w-full overflow-hidden flex flex-col gap-1 py-0.5">
            <div class="flex items-start justify-between gap-2">
              <p class="text-sm font-medium truncate leading-tight">
                {{ item.display }}
              </p>
              <span
                v-if="item.dateLabel"
                class="text-[10px] opacity-60 shrink-0 leading-tight"
              >
                {{ item.dateLabel }}
              </span>
            </div>
            <p
              v-if="item.project"
              class="text-[11px] opacity-70 truncate leading-tight"
            >
              <span
                v-if="item.project.projectCode"
                class="font-mono text-primary-600 dark:text-primary-400"
              >{{ item.project.projectCode }}</span>
              <span
                v-if="item.project.projectCode && item.project.display"
                class="mx-1 opacity-40"
              >·</span>
              <span v-if="item.project.display">{{ item.project.display }}</span>
            </p>
            <div class="flex items-center gap-3 min-w-0">
              <div
                v-if="item.evaluated"
                class="flex items-center gap-1.5 min-w-0"
              >
                <ResourceImage
                  :resource="item.evaluated"
                  width="w-6"
                  height="h-6"
                  disable-link
                />
                <div class="min-w-0">
                  <p class="text-[10px] opacity-50 leading-none mb-0.5">
                    {{ t('components.evaluation.list.filters.assesees') }}
                  </p>
                  <p class="text-[11px] truncate leading-tight">
                    {{ item.evaluated.firstname }} {{ item.evaluated.lastname }}
                  </p>
                </div>
              </div>
              <div
                v-if="item.evaluators?.length"
                class="flex items-center gap-1.5 min-w-0"
              >
                <div class="flex -space-x-1.5">
                  <ResourceImage
                    v-for="evaluator in item.evaluators.slice(0, 3)"
                    :key="evaluator._id"
                    :resource="evaluator"
                    width="w-6"
                    height="h-6"
                    class="ring-1 ring-base rounded-full"
                    disable-link
                  />
                </div>
                <div class="min-w-0">
                  <p class="text-[10px] opacity-50 leading-none mb-0.5">
                    {{ t('components.evaluation.list.evaluationCard.evaluators') }}
                  </p>
                  <p
                    v-if="item.evaluators.length === 1"
                    class="text-[11px] truncate leading-tight"
                  >
                    {{ item.evaluators[0].firstname }} {{ item.evaluators[0].lastname }}
                  </p>
                  <p
                    v-else
                    class="text-[11px] opacity-70 leading-tight"
                  >
                    +{{ item.evaluators.length }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Combobox>

      <Combobox
        v-else
        v-model="selectedItem"
        v-model:search="searchProcess"
        v-model:limit="limitProcess"
        v-model:page="pageProcess"
        v-model:have-more="haveMoreProcess"
        :loading="isLoadingProcess"
        :options="listProcess"
        :placeholder="t('modules.chat.embed.search')"
        size="small"
        @search="getMoreProcess"
      >
        <template #custom-item="{ item }">
          <div class="min-w-0 flex-1 flex items-center gap-2">
            <ListItemResource
              v-if="item.candidate"
              :resource="item.candidate"
            />
            <span
              v-else
              class="text-sm truncate"
            >{{ item.display }}</span>
            <span
              v-if="item.position?.display"
              class="text-[11px] opacity-60 truncate ml-auto"
            >
              {{ item.position.display }}
            </span>
          </div>
        </template>
      </Combobox>
    </div>
  </div>
</template>
